import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Checkbox, FormControlLabel, Dialog } from '@mui/material';
import { styled } from '@mui/system';
import { selectAccessories } from '../features/accessories/accessoriesSlice';
import { useSelector } from 'react-redux';
import { selectComfortDare } from '../features/comfort/comfortDare/comfortDareSlice';
import { selectExploreDare } from '../features/explore/exploreDare/exploreDareSlice';
import { selectDemoDare } from '../features/demo/demoDare/demoDareSlice';
import { selectOpenDare } from '../features/open/openDare/openDareSlice';

const StyledDialogContent = styled(Box)(({ theme }) => ({
  padding: '24px',
  background: `
    radial-gradient(ellipse at 20% 10%, rgba(89, 18, 51, 0.22) 0%, transparent 52%),
    radial-gradient(ellipse at 80% 90%, rgba(64, 13, 41, 0.18) 0%, transparent 48%),
    rgba(26, 8, 20, 0.95)
  `,
  borderRadius: '20px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.55)',
  backdropFilter: 'blur(12px)',
  border: '1px solid rgba(89, 18, 51, 0.22)',

  width: '100%',
  maxWidth: 'min(90vw, 400px)',
  minWidth: '280px',
  maxHeight: '90vh',
  overflowY: 'auto',
  overflowX: 'hidden',
  boxSizing: 'border-box',

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',

  opacity: 0,
  transform: 'scale(0.95)',
  animation: 'fadeInScale 300ms ease forwards',
  zIndex: 1000,
  position: 'relative',

  '@keyframes fadeInScale': {
    to: { opacity: 1, transform: 'scale(1)' },
  },
}));

const StyledHeader = styled(Typography)({
  fontFamily: 'Playfair Display, serif',
  fontSize: '1.3rem',
  color: '#d48aae',
  textAlign: 'center',
  marginBottom: '20px',
  letterSpacing: '0.08em',
  textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
});

const StyledFormControlLabel = styled(FormControlLabel)({
  marginBottom: '6px',
  marginLeft: 0,
  marginRight: 0,
  padding: '6px 10px',
  borderRadius: '50px',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  transition: 'background 0.25s ease',
  '&:hover': {
    background: 'rgba(160, 55, 100, 0.08)',
  },
  '.MuiTypography-root': {
    fontFamily: 'Playfair Display, serif',
    fontSize: '0.95rem',
    color: '#d48aae',
    letterSpacing: '0.04em',
  },
});

const StyledCheckbox = styled(Checkbox)({
  color: 'rgba(160, 55, 110, 0.45)',
  padding: '4px 8px',
  transition: 'all 0.2s ease',
  '&.Mui-checked': {
    color: '#d48aae',
  },
});

const StyledButton = styled(Button)({
  fontFamily: 'Playfair Display, serif',
  fontWeight: 500,
  fontSize: '0.88rem',
  letterSpacing: '0.07em',
  textTransform: 'none',
  color: '#d48aae',
  background: `
    radial-gradient(ellipse at 50% 0%, rgba(160, 55, 120, 0.18), transparent 65%),
    linear-gradient(to bottom, #2e0f20 0%, #1e0916 55%, #160710 100%)
  `,
  border: '1px solid rgba(160, 55, 110, 0.28)',
  borderRadius: '50px',
  padding: '9px 22px',
  boxShadow: '0 0 14px rgba(140, 40, 95, 0.15), 0 3px 10px rgba(0, 0, 0, 0.5)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'scale(1.03)',
    boxShadow: '0 0 20px rgba(155, 50, 110, 0.25), 0 4px 14px rgba(0, 0, 0, 0.58)',
  },
});

const CheckboxList = styled(Box)({
  flex: 1,
  width: '100%',
  maxHeight: '60vh',
  overflowY: 'auto',
  padding: '0 8px',
  marginBottom: '16px',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(160, 55, 110, 0.35) transparent',
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(160, 55, 110, 0.35)',
    borderRadius: '8px',
  },
});




const AccessoryCardPopover = ({open, onClose, onStartGame, onAccessoryChange, gameType }) => {
  const [selectedAccessories, setselectedAccessories] = useState([]);
  const accessoriesSelector = useSelector(selectAccessories)
  const demoDareSelector = useSelector(selectDemoDare)
  const comfortDareSelector = useSelector(selectComfortDare)
  const exploreDareSelector = useSelector(selectExploreDare)
  const openDareSelector = useSelector(selectOpenDare)
  
  useEffect(() => {
    if (gameType === 'explore'){
      setselectedAccessories(exploreDareSelector.Accessories)
    }else if (gameType === 'comfort'){
      setselectedAccessories(comfortDareSelector.Accessories)
    } else if (gameType === 'demo'){
      setselectedAccessories(demoDareSelector.Accessories)
    } else{
      setselectedAccessories(openDareSelector.Accessories)
    }
  }, [])
  
  const handleToggle = (item) => {
    setselectedAccessories((prevItems) =>
      prevItems.includes(item) ? prevItems.filter((i) => i !== item) : [...prevItems, item]
    );
  };

  const handleStartGame = () => {
    onStartGame(selectedAccessories);
    onClose();
  };
  const handleAccessoryChange = () => {
    onAccessoryChange(selectedAccessories);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          background: 'none',
          boxShadow: 'none',
          borderRadius: 0,
          margin: 0,
          maxWidth: '90vw',    // ✅ Critical!
          overflowX: 'hidden',  // ✅ Eliminate extra scroll
        },
      }}
    >
      <StyledDialogContent>
        <StyledHeader>Select Items</StyledHeader>

        <CheckboxList>
  {accessoriesSelector[gameType]?.map((item) => (
    <StyledFormControlLabel
      key={item}
      control={
        <StyledCheckbox
          checked={selectedAccessories.includes(item)}
          onChange={() => handleToggle(item)}
        />
      }
      label={item}
    />
  ))}
</CheckboxList>

        <Box
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <StyledButton onClick={onClose}>Back</StyledButton>
          {onStartGame && (
            <StyledButton onClick={handleStartGame}>Start Game</StyledButton>
          )}
          {onAccessoryChange && (
            <StyledButton onClick={handleAccessoryChange}>Change Accessories</StyledButton>
          )}
        </Box>
      </StyledDialogContent>
    </Dialog>
  );
};

export default AccessoryCardPopover;